import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Storefront, Tray, Check } from "@phosphor-icons/react";

const SCREENS = [
  {
    id: "hours",
    Icon: Clock,
    title: "Hours & service areas",
    text: "Set each day's hours, mark days closed, and add the cities you serve. Changes publish to your website.",
    src: "/portal/hours.jpg",
    alt: "Command Center — business hours table and service-area tags",
    width: 820,
    height: 640,
  },
  {
    id: "business",
    Icon: Storefront,
    title: "Business info, logo & photos",
    text: "Edit your name, phone, email, about copy, and services; upload your logo and job photos — no file-sending required.",
    src: "/portal/business.jpg",
    alt: "Command Center — business details and logo upload screen",
    width: 820,
    height: 580,
  },
  {
    id: "leads",
    Icon: Tray,
    title: "Leads",
    text: "Every quote and contact request from your site lands here with a status — New, Contacted, Booked, Completed — alongside the email alert you already get.",
    src: "/portal/leads.jpg",
    alt: "Command Center — leads list with status filters",
    width: 1080,
    height: 380,
  },
];

const WE_HANDLE = ["Design, layout & new pages", "Hosting, SSL & security", "Performance & uptime", "Lead routing", "Technical support"];

export default function Portal() {
  const [active, setActive] = useState(0);
  const screen = SCREENS[active];

  return (
    <section
      id="portal"
      data-tone="light"
      data-testid="portal-section"
      className="tone-light relative py-16 sm:py-24 md:py-32 bg-[#f6f5f1] border-b border-zinc-200 scroll-mt-16 md:scroll-mt-20"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase text-brand-700">
            <span className="block w-8 h-px bg-brand-700" />
            Command Center
            <span className="hidden sm:inline normal-case tracking-normal text-zinc-600">· Included with websites we build</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-display font-bold text-[34px] sm:text-5xl lg:text-[52px] leading-[1.02] tracking-tight text-zinc-900"
          >
            Your business info stays in your hands.
          </motion.h2>
          <p className="mt-5 text-[15px] sm:text-base text-zinc-600 leading-relaxed max-w-2xl">
            The Command Center is the private portal behind every WebJesters website. Update the things that change
            yourself, any time, and see them on your live site — no emailing us, no waiting.
          </p>
        </div>

        <div className="mt-8 sm:mt-14 grid lg:grid-cols-12 gap-5 lg:gap-10 items-start">
          <div className="lg:col-span-4 grid gap-2.5 sm:gap-3" role="tablist" aria-label="Command Center functions">
            {SCREENS.map((s, i) => {
              const on = i === active;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setActive(i)}
                  data-testid={`portal-tab-${s.id}`}
                  className={`text-left rounded-2xl sm:rounded-3xl border p-4 sm:p-6 transition-colors ${
                    on ? "border-brand-500/60 bg-white shadow-[0_18px_40px_-24px_rgba(24,24,27,0.25)]" : "border-zinc-200 bg-white/60 hover:border-zinc-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border ${on ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-900"}`}>
                      <s.Icon size={16} weight="regular" />
                    </span>
                    <h3 className="text-zinc-900 text-[15.5px]">{s.title}</h3>
                  </div>
                  <p className={`mt-3 text-[13.5px] leading-relaxed ${on ? "text-zinc-700" : "hidden lg:block text-zinc-600"}`}>{s.text}</p>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-8">
            <div
              data-testid="portal-screenshot-frame"
              className="rounded-3xl border border-zinc-900/10 bg-[#0e0e10] overflow-hidden shadow-[0_40px_90px_-30px_rgba(24,24,27,0.45)]"
            >
              <div className="flex items-center gap-2 px-4 h-10 border-b border-white/[0.07] bg-[#141417]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-[11.5px] text-zinc-400 truncate">portal.webjesters.com · {screen.title}</span>
              </div>
              <div className="relative bg-[#050506] aspect-[16/10] sm:aspect-[41/26]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={screen.id}
                    src={screen.src}
                    alt={screen.alt}
                    width={screen.width}
                    height={screen.height}
                    loading="lazy"
                    decoding="async"
                    data-testid="portal-screenshot"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 w-full h-full object-contain object-top p-3 sm:p-5"
                  />
                </AnimatePresence>
              </div>
            </div>
            <p data-testid="portal-screenshot-caption" className="mt-3 text-[12.5px] text-zinc-600">
              Actual Command Center screens, shown with sample data.
            </p>
          </div>
        </div>

        <div
          data-testid="portal-we-handle"
          className="mt-6 sm:mt-8 card-light rounded-3xl p-5 sm:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-8"
        >
          <div className="text-[11px] tracking-[0.22em] uppercase text-brand-700 md:w-40 flex-shrink-0">We handle</div>
          <ul className="flex flex-wrap gap-x-6 gap-y-2.5">
            {WE_HANDLE.map((item) => (
              <li key={item} className="inline-flex items-center gap-2 text-[14px] text-zinc-800">
                <Check size={13} weight="bold" className="text-brand-700" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p data-testid="portal-footer-note" className="mt-6 text-[13px] text-zinc-600 leading-relaxed max-w-2xl">
          Portal access is part of the website plan. Google Business Profile setup and Local Visibility customers are
          served directly by our team and don&apos;t need the portal.
        </p>
      </div>
    </section>
  );
}
